<script setup lang="ts">
import { ref, onMounted } from "vue";
import DataTable from "./DataTable.vue";
import Modal from "./Modal.vue";
import Button from "./Button.vue";
import services from "@/services";
import { useToaster } from "ram-components";
import { useAuthStore } from "@/stores/authStore";


const toaster = useToaster();
const authStore = useAuthStore();

type ModalMode = "create" | "edit";

const reasons = ref<any[]>([]);
const showModal = ref(false);
const modalMode = ref<ModalMode>("create");
const loading = ref(false);
const saving = ref(false);

const selectedId = ref(0);
const reasonText = ref("");
const isActive = ref(true);

const columns = [
  { key: "reason", label: "Reason" },
  { key: "isActive", label: "Status" },
  { key: "action", label: "Action" },
];

onMounted(() => {
  fetchReasons();
});

async function fetchReasons() {
  loading.value = true;
  try {
    const response = await services.getClosureReasons();
    const result = response.data.result || [];

    reasons.value = result.map((item: any) => ({
      recordID: item.recordID,
      reason: item.reason,
      isActive: item.status === "Active",
    }));
  } catch (error) {
    toaster.push({ type: "error", text: "Failed to load reasons" });
  } finally {
    loading.value = false;
  }
}

function openCreateModal() {
  modalMode.value = "create";
  selectedId.value = 0;
  reasonText.value = "";
  isActive.value = true;

  showModal.value = true;
}

async function openEditModal(row: any) {
  modalMode.value = "edit";
  selectedId.value = row.recordID;

  try {
    const response = await services.getClosureReasonById(
      row.recordID,
    );
    const details = response.data.result;

    if (details) {
      reasonText.value = details.reasonClosureName;
      isActive.value = details.isActive;
      showModal.value = true;
    }
  } catch (error) {
    toaster.push({ 
      type: "error",
       text: "Failed to fetch details" 
      });
  } finally {
  }
}

async function handleSave() {
  saving.value = true;

  const payload = {
    recordID: modalMode.value === "create" ? 0 : selectedId.value,
    reasonClosureName: reasonText.value,
    isActive: isActive.value,
    createdBy: authStore.currentUserID
  };

  try {
    if (modalMode.value === "create") {
      await services.saveClosureReason(payload);
      toaster.push({ type: "success", text: "Created successfully" });
    
    } else {
      await services.saveClosureReason(payload);
      toaster.push({ type: "success", text: "Updated successfully" });
    }

    showModal.value = false;
    await fetchReasons();
  } catch (error: any) {
    const apiData = error.response?.data;
    if (
      apiData?.message === "Closure Reason already exists"
    ) {
      showModal.value = false;
      toaster.push({
        type: "warning",
        text: "Closure Reason already exists",
      });
    } else {
      toaster.push({
        type: "error",
        text: apiData?.message || "Failed to save details",
      });
    }
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="rounded-xl border bg-white">
    <div class="flex items-center justify-between border-b p-4">
      <h2 class="font-semibold text-gray-900">Closure Reasons</h2>
      <button
        @click="openCreateModal"
        class="rounded-lg bg-ram-red px-4 py-2 text-sm text-white hover:bg-red-700"
      >
        Add Reason
      </button>
    </div>

    <DataTable
      :columns="columns"
      :data="reasons"
      empty-message="No Reasons Added"
      :loading="loading"
    >
      <template #cell-isActive="{ row }">
        <span
          :class="[
            'rounded-full px-2 py-1 text-xs font-medium',
            row.isActive
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700',
          ]"
        >
          {{ row.isActive ? "Active" : "Inactive" }}
        </span>
      </template>

      <template #cell-action="{ row }">
        <button
          class="text-sm font-medium text-blue-600 hover:underline"
          @click="openEditModal(row)"
        >
          Edit
        </button>
      </template>
    </DataTable>
  </div>

  <Modal
    :is-open="showModal"
    :title="modalMode === 'create' ? 'Add Reason' : 'Edit Reason'"
    size="lg"
    @close="showModal = false"
  >
    <div class="space-y-4 py-2">
      <FormKit
        v-model="reasonText"
        label="Reason Text"
        v-limit="60"
        placeholder="eg. Consignment cancelled"
        validation="required | length:0,60"
        validation-visibility="live"
        :disabled="modalMode === 'edit'"
        type="text"
        input-class="w-full px-2 py-1 my-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
        label-class=" text-sm font-medium text-gray-700 block"
        :classes="{
          messages: 'text-red-600 text-xs mt-1',
          inputInvalid: 'border-red-500 focus:ring-red-500',
        }"
      />

      <div v-if="modalMode === 'edit'" class="flex items-center">
        <FormKit
          type="checkbox"
          v-if="modalMode === 'edit'"
          name="isActive"
          v-model="isActive"
          label="Active"
          :classes="{
            input:
              'h-4 w-4 mr-2 rounded border-gray-900 text-ram-red focus:ring-ram-red',
            label: 'text-sm font-medium text-gray-700 cursor-pointer',
          }"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <Button variant="secondary" @click="showModal = false"> Cancel </Button>
        <Button
          variant="ram"
          @click="handleSave"
          :disabled="!reasonText || saving || reasonText.length > 60"
        >
          {{ saving ? "Saving..." : "Save" }}
        </Button>
      </div>
    </template>
  </Modal>
</template>
